import { collection, getDocs, query, where, orderBy, limit, doc, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase/config';
import { subnetNames } from '../utils/subnetNames';

export interface DelegationEvent {
  action: 'DELEGATE' | 'UNDELEGATE';
  delegate_ss58: string;
  nominator_ss58: string;
  timestamp: string;
  usd: string;
  amount?: string; // TAO amount in planck
}

export interface DelegationAnalysis {
  subnet: number;
  subnetName: string;
  totalBuyUsd: number;
  totalSellUsd: number;
  netFlowUsd: number;
  recentTransactions: DelegationEvent[];
  largestBuys: {
    amount: number;
    wallet: string;
    timestamp: string;
  }[];
  largestSells: {
    amount: number;
    wallet: string;
    timestamp: string;
  }[];
}

/**
 * Get delegation history for a specific subnet
 */
export async function getSubnetDelegationHistory(
  netuid: number,
  timeframeHours: number = 24
): Promise<DelegationEvent[]> {
  try {
    // Calculate the timestamp for the start of the timeframe
    const startTime = new Date();
    startTime.setHours(startTime.getHours() - timeframeHours);
    const startTimeString = startTime.toISOString();

    // Get all events from the delegation_history collection for this subnet
    const eventsRef = collection(db, 'delegation_history', netuid.toString(), 'events');
    const q = query(
      eventsRef,
      where('timestamp', '>=', startTimeString),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log(`No delegation history found for subnet ${netuid} in the last ${timeframeHours} hours`);
      return [];
    }

    // Map the documents to our interface
    const events: DelegationEvent[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        action: data.action,
        delegate_ss58: data.delegate_ss58,
        nominator_ss58: data.nominator_ss58,
        timestamp: data.timestamp,
        usd: data.usd,
        amount: data.amount || undefined
      };
    });

    console.log(`Found ${events.length} delegation events for subnet ${netuid}`);
    return events;
  } catch (error) {
    console.error(`Error fetching delegation history for subnet ${netuid}:`, error);
    return [];
  }
}

/**
 * Filter out matching delegation/undelegation pairs with USD amounts within 10 cents
 * or with the exact same timestamp
 */
function filterMatchingDelegations(events: DelegationEvent[]): DelegationEvent[] {
  // Create maps to track delegations and undelegations
  const delegations: DelegationEvent[] = [];
  const undelegations: DelegationEvent[] = [];
  
  // Split events into delegations and undelegations
  events.forEach(event => {
    if (event.action === 'DELEGATE') {
      delegations.push(event);
    } else {
      undelegations.push(event);
    }
  });
  
  // Find matching pairs to exclude
  const excludeEvents = new Set<DelegationEvent>();
  
  // For each delegation, look for a matching undelegation
  delegations.forEach(delegation => {
    const delegationUsd = parseFloat(delegation.usd);
    
    // Find undelegations with the same timestamp
    const sameTimeUndelegations = undelegations.filter(undelegation => 
      undelegation.timestamp === delegation.timestamp
    );
    
    // If there are any undelegations with the same timestamp, exclude both
    if (sameTimeUndelegations.length > 0) {
      // First check for exact USD matches or within 10 cents
      const matchingUndelegation = sameTimeUndelegations.find(undelegation => {
        const undelegationUsd = parseFloat(undelegation.usd);
        const difference = Math.abs(delegationUsd - undelegationUsd);
        return difference <= 0.10; // Within 10 cents
      });
      
      if (matchingUndelegation) {
        // Found a matching pair, exclude both
        excludeEvents.add(delegation);
        excludeEvents.add(matchingUndelegation);
      }
    }
  });
  
  // Return events that aren't in the exclude set
  return events.filter(event => !excludeEvents.has(event));
}

/**
 * Analyze delegation patterns across all subnets
 */
export async function analyzeDelegationPatterns(
  timeframeHours: number = 24,
  minUsdThreshold: number = 1000
): Promise<DelegationAnalysis[]> {
  try {
    // Get all subnets with delegation history
    const subnets = Object.keys(subnetNames).map(id => parseInt(id));
    
    // Process each subnet
    const analysisPromises = subnets.map(async (netuid) => {
      const events = await getSubnetDelegationHistory(netuid, timeframeHours);
      
      if (events.length === 0) {
        return null;
      }
      
      // Filter out matching delegation/undelegation pairs
      const filteredEvents = filterMatchingDelegations(events);
      
      if (filteredEvents.length === 0) {
        return null;
      }
      
      // Calculate total buy and sell amounts
      let totalBuyUsd = 0;
      let totalSellUsd = 0;
      
      // Track large transactions
      const buys: {amount: number, wallet: string, timestamp: string}[] = [];
      const sells: {amount: number, wallet: string, timestamp: string}[] = [];
      
      filteredEvents.forEach(event => {
        const usdAmount = parseFloat(event.usd);
        
        if (isNaN(usdAmount)) return;
        
        if (event.action === 'DELEGATE') {
          totalBuyUsd += usdAmount;
          
          if (usdAmount >= minUsdThreshold) {
            buys.push({
              amount: usdAmount,
              wallet: event.nominator_ss58,
              timestamp: event.timestamp
            });
          }
        } else {
          totalSellUsd += usdAmount;
          
          if (usdAmount >= minUsdThreshold) {
            sells.push({
              amount: usdAmount,
              wallet: event.nominator_ss58,
              timestamp: event.timestamp
            });
          }
        }
      });
      
      // Calculate net flow (buy volume - sell volume)
      const netFlowUsd = totalBuyUsd - totalSellUsd;
      
      // Sort transactions by amount (descending)
      buys.sort((a, b) => b.amount - a.amount);
      sells.sort((a, b) => b.amount - a.amount);
      
      return {
        subnet: netuid,
        subnetName: subnetNames[netuid] || `Subnet ${netuid}`,
        totalBuyUsd,
        totalSellUsd,
        netFlowUsd,
        recentTransactions: filteredEvents.slice(0, 10), // Keep the 10 most recent transactions
        largestBuys: buys.slice(0, 5), // Top 5 largest buys
        largestSells: sells.slice(0, 5) // Top 5 largest sells
      };
    });
    
    const results = await Promise.all(analysisPromises);
    
    // Filter out null results and sort by net flow (descending)
    return results
      .filter((result): result is DelegationAnalysis => result !== null)
      .sort((a, b) => b.netFlowUsd - a.netFlowUsd);
  } catch (error) {
    console.error('Error analyzing delegation patterns:', error);
    return [];
  }
}

/**
 * Get the top wallets by delegation activity
 */
export async function getTopDelegators(
  timeframeHours: number = 24,
  limit: number = 10
): Promise<{
  wallet: string;
  totalBuyUsd: number;
  totalSellUsd: number;
  netFlowUsd: number;
  subnetsActive: number[];
}[]> {
  try {
    // Get all subnets with delegation history
    const subnets = Object.keys(subnetNames).map(id => parseInt(id));
    
    // Collect all events across subnets
    const allEventsPromises = subnets.map(netuid => getSubnetDelegationHistory(netuid, timeframeHours));
    const allSubnetEvents = await Promise.all(allEventsPromises);
    
    // Combine all events
    const allEvents: {event: DelegationEvent, subnet: number}[] = [];
    allSubnetEvents.forEach((events, index) => {
      const subnet = subnets[index];
      // Filter out matching delegation/undelegation pairs for each subnet
      const filteredEvents = filterMatchingDelegations(events);
      
      filteredEvents.forEach(event => {
        allEvents.push({
          event,
          subnet
        });
      });
    });
    
    // Group by wallet address
    const walletActivity: Record<string, {
      totalBuyUsd: number;
      totalSellUsd: number;
      subnetsActive: Set<number>;
    }> = {};
    
    allEvents.forEach(({event, subnet}) => {
      const wallet = event.nominator_ss58;
      const usdAmount = parseFloat(event.usd);
      
      if (isNaN(usdAmount)) return;
      
      if (!walletActivity[wallet]) {
        walletActivity[wallet] = {
          totalBuyUsd: 0,
          totalSellUsd: 0,
          subnetsActive: new Set()
        };
      }
      
      walletActivity[wallet].subnetsActive.add(subnet);
      
      if (event.action === 'DELEGATE') {
        walletActivity[wallet].totalBuyUsd += usdAmount;
      } else {
        walletActivity[wallet].totalSellUsd += usdAmount;
      }
    });
    
    // Convert to array and calculate net flow
    const walletArray = Object.entries(walletActivity).map(([wallet, activity]) => ({
      wallet,
      totalBuyUsd: activity.totalBuyUsd,
      totalSellUsd: activity.totalSellUsd,
      netFlowUsd: activity.totalBuyUsd - activity.totalSellUsd,
      subnetsActive: Array.from(activity.subnetsActive)
    }));
    
    // Sort by total activity (buys + sells) and take top N
    return walletArray
      .sort((a, b) => (b.totalBuyUsd + b.totalSellUsd) - (a.totalBuyUsd + a.totalSellUsd))
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting top delegators:', error);
    return [];
  }
}

/**
 * Find which subnets whales are buying
 */
export async function getWhaleBuyingActivity(
  timeframeHours: number = 24,
  minUsdThreshold: number = 5000
): Promise<{
  subnet: number;
  subnetName: string;
  totalWhaleUsd: number;
  whaleTransactions: {
    amount: number;
    wallet: string;
    timestamp: string;
  }[];
}[]> {
  try {
    const subnetAnalyses = await analyzeDelegationPatterns(timeframeHours, minUsdThreshold);
    
    // Filter to only include subnets with significant whale buying
    const whaleActivity = subnetAnalyses
      .filter(analysis => analysis.largestBuys.length > 0)
      .map(analysis => ({
        subnet: analysis.subnet,
        subnetName: analysis.subnetName,
        totalWhaleUsd: analysis.largestBuys.reduce((sum, tx) => sum + tx.amount, 0),
        whaleTransactions: analysis.largestBuys
      }))
      .sort((a, b) => b.totalWhaleUsd - a.totalWhaleUsd);
    
    return whaleActivity;
  } catch (error) {
    console.error('Error getting whale buying activity:', error);
    return [];
  }
}

/**
 * Find which subnets whales are selling
 */
export async function getWhaleSellingActivity(
  timeframeHours: number = 24,
  minUsdThreshold: number = 5000
): Promise<{
  subnet: number;
  subnetName: string;
  totalWhaleUsd: number;
  whaleTransactions: {
    amount: number;
    wallet: string;
    timestamp: string;
  }[];
}[]> {
  try {
    const subnetAnalyses = await analyzeDelegationPatterns(timeframeHours, minUsdThreshold);
    
    // Filter to only include subnets with significant whale selling
    const whaleActivity = subnetAnalyses
      .filter(analysis => analysis.largestSells.length > 0)
      .map(analysis => ({
        subnet: analysis.subnet,
        subnetName: analysis.subnetName,
        totalWhaleUsd: analysis.largestSells.reduce((sum, tx) => sum + tx.amount, 0),
        whaleTransactions: analysis.largestSells
      }))
      .sort((a, b) => b.totalWhaleUsd - a.totalWhaleUsd);
    
    return whaleActivity;
  } catch (error) {
    console.error('Error getting whale selling activity:', error);
    return [];
  }
}

/**
 * Find which subnets are experiencing selling pressure
 */
export async function getSellingPressure(
  timeframeHours: number = 24
): Promise<{
  subnet: number;
  subnetName: string;
  sellRatio: number; // Ratio of sells to total volume
  totalVolumeUsd: number;
}[]> {
  try {
    const subnetAnalyses = await analyzeDelegationPatterns(timeframeHours);
    
    // Calculate sell ratio for each subnet
    const sellPressure = subnetAnalyses
      .filter(analysis => (analysis.totalBuyUsd + analysis.totalSellUsd) > 0)
      .map(analysis => {
        const totalVolume = analysis.totalBuyUsd + analysis.totalSellUsd;
        return {
          subnet: analysis.subnet,
          subnetName: analysis.subnetName,
          sellRatio: analysis.totalSellUsd / totalVolume,
          totalVolumeUsd: totalVolume
        };
      })
      // Only include subnets with significant volume
      .filter(item => item.totalVolumeUsd >= 1000)
      // Sort by sell ratio (descending)
      .sort((a, b) => b.sellRatio - a.sellRatio);
    
    return sellPressure;
  } catch (error) {
    console.error('Error calculating selling pressure:', error);
    return [];
  }
}

/**
 * Find subnets that are being silently accumulated
 * (few transactions but high net inflow)
 */
export async function getSilentAccumulation(
  timeframeHours: number = 24,
  minUsdThreshold: number = 5000,
  minTransactionCount: number = 5,
  maxTransactionCount: number = 20
): Promise<{
  subnet: number;
  subnetName: string;
  netFlowUsd: number;
  transactionCount: number;
  averageTransactionUsd: number;
  largestBuys: {
    amount: number;
    wallet: string;
    timestamp: string;
  }[];
}[]> {
  try {
    const subnetAnalyses = await analyzeDelegationPatterns(timeframeHours, minUsdThreshold);
    
    // List of subnets to exclude
    const excludedSubnets = [
      "Chutes", "Targon", "Proprietary Trading Network", "Gradients", "τemplar", 
      "Celium", "Nineteen", "Nineteen.ai", "Apex", "BitMind", "Data Universe", 
      "NOVA", "Nova", "Dojo", "404-Gen", "EfficientFrontier", "Efficient Frontier"
    ];
    
    // Filter for subnets with:
    // 1. Positive net flow (more buys than sells)
    // 2. Between 5-20 transactions (silent accumulation)
    // 3. High average transaction size (whale activity)
    // 4. Maximum net inflow of $25,000
    // 5. Not in the exclusion list
    const silentAccumulation = subnetAnalyses
      .filter(analysis => {
        // Must have positive net flow
        if (analysis.netFlowUsd <= 0 || analysis.netFlowUsd > 25000) return false;
        
        // Check if subnet is in the exclusion list
        if (excludedSubnets.some(name => analysis.subnetName.includes(name))) return false;
        
        // Count total transactions
        const transactionCount = analysis.recentTransactions.length;
        
        // Must have at least 5 transactions but no more than 20
        if (transactionCount < minTransactionCount || transactionCount > maxTransactionCount) return false;
        
        // Calculate average transaction size
        const avgTxSize = analysis.totalBuyUsd / 
          analysis.recentTransactions.filter(tx => tx.action === 'DELEGATE').length;
        
        // Return true if meets criteria for silent accumulation
        return avgTxSize >= minUsdThreshold;
      })
      .map(analysis => {
        const buyTxCount = analysis.recentTransactions.filter(tx => tx.action === 'DELEGATE').length;
        return {
          subnet: analysis.subnet,
          subnetName: analysis.subnetName,
          netFlowUsd: analysis.netFlowUsd,
          transactionCount: analysis.recentTransactions.length,
          averageTransactionUsd: buyTxCount > 0 ? analysis.totalBuyUsd / buyTxCount : 0,
          largestBuys: analysis.largestBuys
        };
      })
      // Sort by net flow (descending)
      .sort((a, b) => b.netFlowUsd - a.netFlowUsd);
    
    return silentAccumulation;
  } catch (error) {
    console.error('Error detecting silent accumulation:', error);
    return [];
  }
}